import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Modal, message, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { pw9213Api } from '../../services/pw9213Api';
import { usePW9213Mode } from './hooks/usePW9213Mode';
import { usePW9213Record } from './hooks/usePW9213Record';
import { usePW9213Navigation } from './hooks/usePW9213Navigation';
import { usePW9213Lookup } from './hooks/usePW9213Lookup';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import PermissionGuard from '../../components/PermissionGuard';
import { SharedActionBar } from '../../components/SharedActionBar';
import MasterInfo from './components/MasterInfo';
import DetailGrid from './components/DetailGrid';
import './PW9213Form.css';

const PW9213Form: React.FC = () => {
  const navigate = useNavigate();
  const perms = useModulePermission(Modules.PW9213);

  // Hooks
  const { mode, setMode, isEditing } = usePW9213Mode();
  const { names, resolveSingleName, resolveAllNames, clearNames } = usePW9213Lookup();
  const {
    record, formData, details, loading,
    loadRecord, setField, getField,
    updateDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = usePW9213Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (empCode: string, year: string) => {
    const result = await loadRecord(empCode, year);
    if (result) {
      resolveAllNames(result, result.details?.[0]);
    }
  }, [loadRecord, resolveAllNames]);

  const { allKeys, currentIdx, setCurrentIdx, loadList, navTo } = usePW9213Navigation(loadRecordWithNames);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const keys = await loadList();
        if (keys.length > 0) {
          setCurrentIdx(0);
          const [empCode, year] = keys[0].split('|');
          await loadRecordWithNames(empCode, year);
        }
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ──

  const handleAdd = useCallback(() => {
    clearNames();
    resetForAdd();
    setMode('add');
  }, [clearNames, resetForAdd, setMode]);

  const handleModify = useCallback(() => {
    if (!record) return;
    setMode('edit');
  }, [record, setMode]);

  const handleSaveAction = useCallback(() => {
    handleSave(mode, (result) => {
      applyResult(result);
      setMode('browse');
      loadList().then(keys => {
        const key = `${result.f913_01}|${result.f913_02}`;
        const idx = keys.indexOf(key);
        if (idx >= 0) setCurrentIdx(idx);
      });
    });
  }, [mode, handleSave, applyResult, setMode, loadList, setCurrentIdx]);

  const handleCancel = useCallback(() => {
    revertToRecord();
    setMode('browse');
  }, [revertToRecord, setMode]);

  const handleDeleteRecord = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除員工「${record.f913_01}」年度「${record.f913_02}」的考勤記錄？所有明細將一併刪除。`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const keys = await loadList();
          if (keys.length > 0) {
            const newIdx = Math.min(currentIdx, keys.length - 1);
            setCurrentIdx(newIdx);
            const [empCode, year] = keys[newIdx].split('|');
            await loadRecordWithNames(empCode, year);
          } else {
            setCurrentIdx(-1);
            clearNames();
            resetForAdd();
          }
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, clearNames, resetForAdd]);

  const handleCopy = useCallback(async () => {
    if (!record) return;
    try {
      const result = await pw9213Api.copy(record.f913_01, record.f913_02);
      message.success(`複製成功: ${result.f913_01} / ${result.f913_02}`);
      const keys = await loadList();
      const key = `${result.f913_01}|${result.f913_02}`;
      const idx = keys.indexOf(key);
      if (idx >= 0) {
        setCurrentIdx(idx);
        await loadRecordWithNames(result.f913_01, result.f913_02);
      }
    } catch {
      message.error('複製失敗');
    }
  }, [record, loadList, setCurrentIdx, loadRecordWithNames]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) return;
    try {
      const res = await pw9213Api.search(keyword.trim());
      if ((res.data || []).length === 0) {
        message.info('查無資料');
        return;
      }
      // Rebuild allKeys from search result
      const keys = (res.data || []).map(r => `${r.f913_01}|${r.f913_02}`);
      setCurrentIdx(0);
      const [empCode, year] = keys[0].split('|');
      await loadRecordWithNames(empCode, year);
    } catch {
      message.error('查詢失敗');
    }
  }, [setCurrentIdx, loadRecordWithNames]);

  // ── Search state (local only) ──
  const [searchText, setSearchText] = React.useState('');

  return (
    <PermissionGuard module={Modules.PW9213} action="query">
      <Spin spinning={loading}>
        <div className="pw9213-form">
          <div className="page-wrapper">

            {/* Title */}
            <div className="page-title-bar">
              <h1>員工年度考勤記錄 (pw9213f1)</h1>
              <span className="nav-info">
                {allKeys.length > 0
                  ? `${currentIdx + 1} / ${allKeys.length}`
                  : '無資料'}
              </span>
            </div>

            {/* SharedActionBar */}
            <SharedActionBar
              mode={mode}
              isEditing={isEditing}
              hasRecord={!!record}
              currentIdx={currentIdx}
              recordCount={allKeys.length}
              onNavFirst={() => handleNavTo(0)}
              onNavPrev={() => handleNavTo(currentIdx - 1)}
              onNavNext={() => handleNavTo(currentIdx + 1)}
              onNavLast={() => handleNavTo(allKeys.length - 1)}
              onAdd={handleAdd}
              onModify={handleModify}
              onDelete={handleDeleteRecord}
              onSave={handleSaveAction}
              onCancel={handleCancel}
              onCopy={handleCopy}
              onExit={() => navigate('/erp')}
              canCreate={perms.canCreate}
              canEdit={perms.canEdit}
              canDelete={perms.canDelete}
            />

            {/* Search Bar */}
            <div className="search-bar">
              <Input
                placeholder="輸入員工代號或年度查詢"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onPressEnter={() => handleSearch(searchText)}
                disabled={isEditing}
                style={{ width: 240 }}
                maxLength={20}
                suffix={<SearchOutlined />}
              />
              <button
                className="btn"
                disabled={isEditing}
                onClick={() => handleSearch(searchText)}
              >
                查詢
              </button>
              <button
                className="btn"
                disabled={isEditing}
                onClick={async () => {
                  setSearchText('');
                  const keys = await loadList();
                  if (keys.length > 0) {
                    setCurrentIdx(0);
                    const [empCode, year] = keys[0].split('|');
                    await loadRecordWithNames(empCode, year);
                  }
                }}
              >
                全部
              </button>
            </div>

            {/* Form Content */}
            <div className="form-content">
              {/* Master Info */}
              <div className="section-divider">基本資料</div>
              <MasterInfo
                formData={formData}
                names={names}
                isEditing={isEditing}
                getField={getField}
                setField={setField}
                resolveSingleName={resolveSingleName}
                getFieldError={getFieldError}
                mode={mode}
              />

              {/* Detail Grid */}
              <div className="section-divider" style={{ marginTop: 16 }}>假別明細</div>
              <DetailGrid
                details={details}
                isEditing={isEditing}
                names={names}
                updateDetailField={updateDetailField}
                onAdd={addDetailRow}
                onDelete={deleteDetailRow}
                resolveSingleName={resolveSingleName}
              />
            </div>

          </div>
        </div>
      </Spin>
    </PermissionGuard>
  );
};

export default PW9213Form;
